/**
 * JSONL Writers
 *
 * @module @chatsdet/diagnostic-workspace/jsonl-writers
 * @description Dump per-domain data to JSONL files in the workspace. Minimal transformation
 * — the agent uses `jq` to filter. Each writer is independent and idempotent.
 *
 * Inputs use ISO timestamp strings (matching the `RunData` JSON contract); writers convert
 * to ms internally where needed.
 */

import { resolveWorkspacePath, thoughtsByStepRelativePath, WORKSPACE_PATHS } from './paths.js';
import type { RunStepLike } from './timeline-writers.js';
import type { RunDataStepDiagnostics } from './run-data.js';
import { isObject, isoToMs, writeJson, writeJsonl, writeText } from './writer-utils.js';

export {
  writeTimelineActionsAndNavigation,
  writeTimelineSteps,
  type ActionTimelineEntry,
  type ParsedTimeline,
  type RunStepLike,
} from './timeline-writers.js';

// ============================================================================
// timeline/assertions.jsonl + cache-decisions.jsonl (derived from step metadata)
// ============================================================================

export const writeAssertionsAndCacheDecisions = async (
  workspaceRoot: string,
  steps: ReadonlyArray<RunStepLike>
): Promise<void> => {
  const sortedSteps = steps.slice().sort((a, b) => a.orderIndex - b.orderIndex);

  const assertions: Array<Record<string, unknown>> = [];
  const cacheDecisions: Array<Record<string, unknown>> = [];

  for (const step of sortedSteps) {
    const meta = isObject(step.metadataJson) ? step.metadataJson : {};
    const startedMs = isoToMs(step.startedAt);

    const rawAssertions = Array.isArray(meta.assertionResults)
      ? (meta.assertionResults as ReadonlyArray<unknown>)
      : [];
    for (const r of rawAssertions) {
      if (!isObject(r)) continue;
      assertions.push({
        stepId: step.id,
        nodeId: step.nodeId,
        orderIndex: step.orderIndex,
        stepStatus: step.status,
        timestampMs: startedMs,
        assertionId: r.inlineAssertionId ?? r.assertionId ?? r.key ?? null,
        condition: r.condition ?? null,
        severity: r.severity ?? null,
        nonBlocking: Boolean(r.nonBlocking),
        passed: Boolean(r.passed),
        reason: r.reason ?? null,
      });
    }

    cacheDecisions.push({
      stepId: step.id,
      nodeId: step.nodeId,
      orderIndex: step.orderIndex,
      stepStatus: step.status,
      timestampMs: startedMs,
      cacheOutcome: meta.cacheOutcome ?? null,
      cacheHitSource: meta.cacheHitSource ?? null,
      cacheProcessingStatus: meta.cacheProcessingStatus ?? null,
      cacheOutcomeReason: meta.cacheOutcomeReason ?? null,
    });
  }

  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.timelineAssertions),
    assertions
  );
  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.timelineCacheDecisions),
    cacheDecisions
  );
};

/** Write runner-produced diagnostics separately from generic step metadata. */
export const writeStepDiagnostics = async (
  workspaceRoot: string,
  steps: ReadonlyArray<RunStepLike>,
  diagnostics: Readonly<Record<string, RunDataStepDiagnostics>>
): Promise<void> => {
  const rows = steps
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .flatMap((step) => {
      const detail = diagnostics[step.id];
      return detail
        ? [{ stepId: step.id, nodeId: step.nodeId, orderIndex: step.orderIndex, ...detail }]
        : [];
    });
  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.timelineStepDiagnostics),
    rows
  );
};

// ============================================================================
// thoughts/all.jsonl + thoughts/by-step/<stepId>.md
// ============================================================================

export interface ThoughtLike {
  id: string;
  stepId: string | null;
  nodeId: string | null;
  toolName: string | null;
  content: string;
  orderIndex: number;
  /** ISO timestamp. */
  createdAt: string;
}

export const writeThoughts = async (
  workspaceRoot: string,
  thoughts: ReadonlyArray<ThoughtLike>
): Promise<void> => {
  const sorted = thoughts.slice().sort((a, b) => a.orderIndex - b.orderIndex);

  const allRows = sorted.map((t) => ({
    id: t.id,
    stepId: t.stepId,
    nodeId: t.nodeId,
    toolName: t.toolName,
    orderIndex: t.orderIndex,
    timestampMs: isoToMs(t.createdAt),
    createdAt: t.createdAt,
    content: t.content,
  }));

  await writeJsonl(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.thoughtsAll), allRows);

  const byStep = new Map<string, ThoughtLike[]>();
  for (const t of sorted) {
    const key = t.stepId ?? '_no-step';
    const list = byStep.get(key) ?? [];
    list.push(t);
    byStep.set(key, list);
  }

  // Write all per-step files concurrently — they target distinct paths.
  const renderStep = ([stepId, stepThoughts]: [string, ThoughtLike[]]): {
    relativePath: string;
    body: string;
  } => {
    const md: string[] = [];
    md.push(`# Thoughts for ${stepId === '_no-step' ? '(no step)' : `step ${stepId}`}`);
    md.push('');
    for (const t of stepThoughts) {
      const tool = t.toolName ? ` [tool: ${t.toolName}]` : '';
      md.push(`## ${t.createdAt}${tool}`);
      md.push('');
      md.push(t.content);
      md.push('');
    }
    return {
      relativePath: thoughtsByStepRelativePath(stepId),
      body: `${md.join('\n')}\n`,
    };
  };
  const rendered = [...byStep.entries()].map(renderStep);
  await Promise.all(
    rendered.map(({ relativePath, body }) =>
      writeText(resolveWorkspacePath(workspaceRoot, relativePath), body)
    )
  );
};

// ============================================================================
// logs/{network,console}.jsonl (raw + errors)
// ============================================================================

export interface NetworkLogLike {
  url: string;
  method: string;
  status: number | null;
  durationMs: number | null;
  requestBody: string | null;
  responseBody: string | null;
  contentType: string | null;
  timestamp?: number | string | null;
  timestampMs?: number | null;
  relativeTimestampMs?: number | null;
}

export interface ConsoleLogLike {
  type: string;
  text: string;
  timestamp: number | string | null;
  timestampMs?: number | null;
  relativeTimestampMs?: number | null;
  location: string | null;
}

const normalizeTimestamp = (ts: number | string | null | undefined): number | null => {
  if (ts === null || ts === undefined) return null;
  if (typeof ts === 'number') return ts;
  const parsed = Date.parse(ts);
  return Number.isFinite(parsed) ? parsed : null;
};

export const writeLogs = async (
  workspaceRoot: string,
  network: ReadonlyArray<NetworkLogLike>,
  consoleLogs: ReadonlyArray<ConsoleLogLike>
): Promise<void> => {
  const networkRows = network.map((e) => ({
    timestampMs: e.timestampMs ?? normalizeTimestamp(e.timestamp ?? null),
    relativeTimestampMs: e.relativeTimestampMs ?? null,
    url: e.url,
    method: e.method,
    status: e.status ?? null,
    durationMs: e.durationMs ?? null,
    requestBody: e.requestBody ?? null,
    responseBody: e.responseBody ?? null,
    contentType: e.contentType ?? null,
  }));
  const networkErrors = networkRows.filter((r) => typeof r.status === 'number' && r.status >= 400);

  await writeJsonl(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.logsNetwork), networkRows);
  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.logsNetworkErrors),
    networkErrors
  );

  const consoleRows = consoleLogs.map((e) => ({
    timestampMs: e.timestampMs ?? normalizeTimestamp(e.timestamp),
    relativeTimestampMs: e.relativeTimestampMs ?? null,
    type: e.type,
    text: e.text,
    location: e.location ?? null,
  }));
  const consoleErrors = consoleRows.filter((r) => r.type === 'error' || r.type === 'warning');

  await writeJsonl(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.logsConsole), consoleRows);
  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.logsConsoleErrors),
    consoleErrors
  );
};

// ============================================================================
// committed-actions.{md,jsonl}
// ============================================================================

export interface CommittedActionLike {
  ts: string;
  executor: string;
  actionType: string;
  target: string;
  pageUrl: string | null;
  success: boolean;
}

/**
 * Mutations a prior cache/L2 layer already committed during THIS run. A
 * destructive row here (a revoke/delete that succeeded) means the target's
 * later absence is an EXPECTED side effect — not evidence a create step failed.
 */
export const writeCommittedActions = async (
  workspaceRoot: string,
  actions: ReadonlyArray<CommittedActionLike>
): Promise<void> => {
  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.committedActionsJsonl),
    actions
  );

  const lines: string[] = ['# Committed actions (prior cache/L2 layers)', ''];
  if (actions.length === 0) {
    lines.push('_No prior-layer (cache_replay / l2_playbook) actions were recorded for this run._');
  } else {
    lines.push(
      'A prior cache/L2 layer already executed these actions during this run.',
      'If one of them deleted/revoked a record, that record being absent later is EXPECTED —',
      'do NOT attribute the absence to a failed upstream "create" step.',
      ''
    );
    for (const a of actions) {
      // `success` here means the action was performed/recorded (recorded
      // post-landing), not that the server returned 2xx — say "performed".
      lines.push(
        `- ${a.ts} · ${a.executor} · ${a.actionType} "${a.target}"${
          a.success ? ' · performed' : ''
        }${a.pageUrl ? ` · ${a.pageUrl}` : ''}`
      );
    }
  }
  await writeText(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.committedActions),
    `${lines.join('\n')}\n`
  );
};

// ============================================================================
// variables/{inputs.json,lineage.jsonl}
// ============================================================================

export const writeVariables = async (
  workspaceRoot: string,
  runMetadata: Record<string, unknown> | null | undefined,
  steps: ReadonlyArray<RunStepLike>
): Promise<void> => {
  const inputs =
    isObject(runMetadata) && isObject(runMetadata.workflowInputValues)
      ? runMetadata.workflowInputValues
      : {};
  await writeJson(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.variablesInputs), inputs);

  const sortedSteps = steps.slice().sort((a, b) => a.orderIndex - b.orderIndex);
  const lineageRows: Array<Record<string, unknown>> = [];
  for (const step of sortedSteps) {
    const meta = isObject(step.metadataJson) ? step.metadataJson : {};
    const captures = Array.isArray(meta.variablesSet) ? meta.variablesSet : [];
    const startedMs = isoToMs(step.startedAt);
    for (const c of captures) {
      if (!isObject(c)) continue;
      lineageRows.push({
        name: c.name ?? c.key ?? null,
        value: c.value ?? null,
        definedAtStepId: step.id,
        definedAtNodeId: step.nodeId,
        definedAtOrderIndex: step.orderIndex,
        definedAtTimestampMs: startedMs,
      });
    }
  }
  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.variablesLineage),
    lineageRows
  );
};

// ============================================================================
// history/recent-runs.jsonl
// ============================================================================

export interface RecentRunLike {
  id: string;
  workflowRunId: string | null;
  status: string;
  /** ISO. */
  startedAt: string | null;
  /** ISO. */
  finishedAt: string | null;
  errorMessage: string | null;
}

export const writeRecentRuns = async (
  workspaceRoot: string,
  runs: ReadonlyArray<RecentRunLike>
): Promise<void> => {
  const rows = runs.map((r) => ({
    testRunId: r.id,
    workflowRunId: r.workflowRunId,
    status: r.status,
    startedAt: r.startedAt,
    finishedAt: r.finishedAt,
    startedAtMs: isoToMs(r.startedAt),
    finishedAtMs: isoToMs(r.finishedAt),
    errorMessage: r.errorMessage,
  }));
  await writeJsonl(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.historyRecentRuns), rows);
};
